import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { faEdit } from '@fortawesome/pro-solid-svg-icons'
import { TwoColumn } from 'components/layout/two-column/TwoColumn'
import { Button } from 'components/buttons'
import { SimpleTable } from 'components/tables'
import { TableButton } from '@features/admin/features/approval-domain-approvers'

interface DefaultGroupManagerRow {
  name: string
  workdayWorkerId: number
}

interface Props {
  approvalGroup: AdminApprovalGroup
  onEdit: () => void
}

const columnHelper = createColumnHelper<DefaultGroupManagerRow>()

export const DefaultGroupManagerSection: FC<Props> = ({
  approvalGroup,
  onEdit,
}) => {
  const { t } = useTranslation()

  const defaultGroupManager = approvalGroup.defaultForDirectReportsOfEmployee

  const data: DefaultGroupManagerRow[] = useMemo(
    () => (defaultGroupManager ? [defaultGroupManager] : []),
    [defaultGroupManager],
  )

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: t('common.name'),
    }),
    columnHelper.accessor('workdayWorkerId', {
      id: 'workdayWorkerId',
      header: t('common.workdayWorkerId'),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.action'),
      cell: () => (
        <TableButton variant="primary" onClick={onEdit}>
          {t('common.edit')}
        </TableButton>
      ),
    }),
  ] as ColumnDef<DefaultGroupManagerRow>[]

  return (
    <TwoColumn
      label={t('features.approvalGroupsAdmin.defaultGroupManager')}
      description={t(
        'features.approvalGroupsAdmin.approvalGroupDetails.defaultGroupManagerDescription',
      )}
    >
      <div className="flex flex-col w-[825px]">
        {defaultGroupManager ? (
          <SimpleTable columns={columns} data={data} />
        ) : (
          <div className="flex flex-col items-center text-sm gap-4 py-8 text-neutral-700 rounded text-center border border-neutral-200">
            {t(
              'features.approvalGroupsAdmin.approvalGroupDetails.noDefaultGroupManager',
            )}
            <Button icon={faEdit} className="text-sm" onClick={onEdit}>
              {t(
                'features.approvalGroupsAdmin.approvalGroupDetails.setDefaultGroupManager',
              )}
            </Button>
          </div>
        )}
      </div>
    </TwoColumn>
  )
}
